import * as vp from 'vopay';
import { Box, SubTitle, Table } from 'vopay';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { TransactionStatus } from 'UI';

import { BillingDetailsContext } from '@/providers/BillingDetails.provider';
import { useTranslation } from 'react-i18next';

type BillingSettingsTablePropsType = {
  key: string;
  value: string | boolean | null;
  type: string;
};

const BillingSettingsList: FunctionComponent<Record<never, never>> = () => {
  const details = useContext(BillingDetailsContext);
  const [billingSettings, setBillingSettings] = useState<vp.BillingSettings>();
  const { t } = useTranslation('translations', { keyPrefix: 'BillingPackage.Details.SettingsList' });

  useEffect(() => {
    setBillingSettings(details?.BillingSettings);
  }, [details]);

  const tableProps: vp.TableProps<BillingSettingsTablePropsType> = {
    id: 'billing-settings-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'settingLabel',
        header: t('tableHeader'),
        align: 'left',
        template: ({ data }) => data.key.split(/(?=[A-Z])/).join(' '),
      },
      {
        id: 'settingValue',
        header: '',
        align: 'left',
        template: ({ data }) => {
          const { value, type, key } = data;

          const keyHandlers: Record<string, () => string> = {
            InvoiceDue: () => (value ? ofTheMonthString(value.toString()) : ''),
            default: () => (value ? value.toString() : ''),
          };
          const handledValue = (keyHandlers[key] || keyHandlers.default)();

          const typeHandlers: Record<string, () => React.ReactNode> = {
            string: () => capitalize(handledValue),
            boolean: () => (
              <TransactionStatus
                label={value ? t('transactionStatusEnabled') : t('transactionStatusDisabled')}
                status={value ? 'positive' : 'negative'}
              />
            ),
            amount: () => (handledValue ? `$${handledValue}` : '$0.00'),
            number: () => (handledValue ? handledValue.padStart(2, '0') : ''),
            month: () => toMonthString(handledValue),
          };

          return typeHandlers[type]();
        },
      },
    ],
    rows: Object.entries(billingSettings || {})
      .filter(([key]) => filterSettings(key))
      .map(([key, { value, type }]) => ({
        id: key,
        data: { key, value, type },
      })),
  };

  return (
    <div>
      <Box mb="md">
        <SubTitle subTitle={t('pageTitle')} />
      </Box>
      <Table {...tableProps} />
    </div>
  );

  function capitalize(str: string) {
    return str
      .split(' ')
      .map((s) => s && s[0].toUpperCase() + s.slice(1))
      .join(' ');
  }

  function toMonthString(str: string) {
    return t('monthsString', { count: parseInt(str, 10) || 0 });
  }

  function ofTheMonthString(str: string) {
    return (
      {
        specific: t('toTheMonthStringSpecific'),
        end: t('toTheMonthStringEnd'),
        beginning: t('toTheMonthStringBeginning'),
      }[str] || ''
    );
  }

  function filterSettings(key: string) {
    const handlers: { [key: string]: () => boolean } = {
      InvoiceDueDate: () => billingSettings?.InvoiceDue.value === 'specific',
      default: () => true,
    };

    const handler = handlers[key] || handlers.default;

    return handler();
  }
};

export default BillingSettingsList;
